import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref } from 'vue';
import {
    orderService
} from './order.service';
import OrderToolbar from '@/ui/views/orders/OrderToolbar.vue';
import QueryParamsService, { QueryParams } from '../../../core/query-params.service';
import SalesDocumentTable from '@/ui/views/salesdocumenttable/SalesDocumentTable.vue';
import { SalesDocumentTableConfig } from '@/ui/views/salesdocumenttable/salesdocument.types';
import { parseISO } from 'date-fns';
import TrackingService from '@/core/tracking.service';
import { store } from '@/store';
import OrderLinesByMaterialViewObject = Vertica.LouisPoulsen.Application.SalesDocuments.ViewModels.OrderLinesByMaterialViewObject;
import { useRouter } from 'vue-router/composables';
import { watchRelativeUrl } from '@/core/route.service';

enum OrderResultMode {
    HasResults,
    NoResults,
    LoadingResults
}


export default /*#__PURE__*/_defineComponent({
  __name: 'OrderView',
  props: {
    pageTitle: null,
    startDateString: null,
    endDateString: null,
    showCustomerColumn: { type: Boolean },
    isUsMarket: { type: Boolean }
  },
  setup(__props: any) {

const props = __props as {
    pageTitle: string,
    startDateString: string,
    endDateString: string,
    showCustomerColumn: boolean,
    isUsMarket: boolean
}



const showingSalesDocumentsByMaterial = computed<boolean>(() => store.getters['orderView/showingSalesDocumentsByMaterial']);
const setShowingSalesDocumentsByMaterial = function(value: boolean) { store.dispatch('orderView/showingSalesDocumentsByMaterial', value); };

const totalAmountOfOrders = ref<number>(0);
const ordersByMaterial = ref<OrderLinesByMaterialViewObject[]>(null);
const totalAmountOfMaterials = ref<number>(0);
const filterValue = ref<string>('');
let timer: any;

let trackRequest = false;
const listImageZoomModalToggled = ref<boolean>(false);
const listImageZoomImages = ref<CarouselImageViewObject[]>([]);

const orderData = ref<OrderHeaderViewObject[] | null>(null);

const showSalesDocumentsByMaterial = computed<boolean>({
    get() {
        return showingSalesDocumentsByMaterial.value;
    },
    set(value: boolean) {
        setShowingSalesDocumentsByMaterial(value);
    }
});

const orders = computed<OrderHeaderViewObject[] | OrderLinesByMaterialViewObject[] | null>(() => {
    if (orderData.value !== null) {
        return showSalesDocumentsByMaterial.value ? ordersByMaterial.value : orderData.value;
    }
    return null;
});

const totalAmount = computed<number>(() => {
    if (orderData.value !== null) {
        return showSalesDocumentsByMaterial.value ? totalAmountOfMaterials.value : totalAmountOfOrders.value;
    }
    return 0;
});

const tableConfig = computed<SalesDocumentTableConfig>(() => {
    if (showSalesDocumentsByMaterial.value) {
        return orderService.tableConfigOrdersByMaterial();
    }
    const config = orderService.tableConfig();

    if (props.showCustomerColumn) {
        config.columns.push({
            headerLabel: 'Pages.PartnerPortal.Orders.OrderPage.OrderTableCustomer',
            widthClass: 'sub-md:u-hidden u-flex-1',
            sortField: 'customerName',
            valueField: 'customerName'
        });
    }

    return config;
});

const tableClass = computed<string>(() => {
    return showSalesDocumentsByMaterial.value ? 'order-table orders-by-material' : 'order-table';
});

const numberOfOrders = computed<number>(() => {
    if (!orders.value) {
        return 0;
    }

    return orders.value.length;
});

const resultMode = computed<OrderResultMode>(() => {
    if (orders.value && orders.value.length > 0) {
        return OrderResultMode.HasResults;
    } else if (orders.value && orders.value.length === 0) {
        return OrderResultMode.NoResults;
    }
    return OrderResultMode.LoadingResults;
});

const hasResults = computed<boolean>(() => {
    return resultMode.value === OrderResultMode.HasResults;
});
const hasNoResults = computed<boolean>(() => {
    return resultMode.value === OrderResultMode.NoResults;
});

const startDate = computed<Date>(() => parseISO(props.startDateString));
const endDate = computed<Date>(() => parseISO(props.endDateString));
const isResultTruncated = computed<boolean>(() => {
    return totalAmount.value > numberOfOrders.value;
});
const fullScreenZoomImages = computed<any[]>(() => listImageZoomImages.value.map(img => ({ imageUrl: img.url })));

onMounted(search);

const router = useRouter();

function onRelativeUrlChange(route: RelativeUrl, oldRoute: RelativeUrl) {
    const triggerChange = route && oldRoute && route.path === oldRoute.path;
    if (!triggerChange) {
        return;
    }

    // Don't update result when navigating away from result page
    const searchQueries = [QueryParams.OrdersStartDate, QueryParams.OrdersEndDate, QueryParams.OrdersOrderNumber, QueryParams.OrdersStatus];
    const changedQueries = QueryParamsService.getChangedQueries(route, oldRoute);

    const hasSearchQueriesChanged = QueryParamsService.hasQueryChanged(searchQueries, changedQueries);

    if (hasSearchQueriesChanged) {
        search(true);
    }
}

watchRelativeUrl(onRelativeUrlChange);

function onFilterValueChange(filterValue) {
    const query = QueryParamsService.appendQueryParam(QueryParams.ClientFilterValue, filterValue);
    router.replace({ query });
}

function search(clearFilterValue = false) {
    orderData.value = null;
    filterValue.value = clearFilterValue ? '' : QueryParamsService.getParam(QueryParams.ClientFilterValue);
    onFilterValueChange(filterValue.value);

    const request: OrderSearchWebRequest = {
        dateFrom: QueryParamsService.getParam(QueryParams.OrdersStartDate),
        dateTo: QueryParamsService.getParam(QueryParams.OrdersEndDate),
        orderNumber: QueryParamsService.getParam(QueryParams.OrdersOrderNumber),
        status: parseInt(QueryParamsService.getParam(QueryParams.OrdersStatus), 10),
        trackRequest
    };

    orderService.orders(request).then((ordersResponse: {orderData: OrderSearchViewModel}) => {
        if (ordersResponse == null) {
            return;
        }
        orderData.value = ordersResponse.orderData.orderHeaders;
        totalAmountOfOrders.value = ordersResponse.orderData.totalCount;
        ordersByMaterial.value = ordersResponse.orderData.ordersByMaterial;
        totalAmountOfMaterials.value = ordersResponse.orderData.ordersByMaterial.length;
    });
}

function onFilter(filterString, total) {
    if (!filterString) { return; }
    if (timer) {
        clearTimeout(timer);
        timer = null;
    }
    timer = setTimeout(() => {
        TrackingService.event('Search on Orders - filter', filterString, total);
    }, 2000);
}

function onFormSubmit() {
    trackRequest = true;
}

function onListImageZoom(images:CarouselImageViewObject[]) {
    listImageZoomModalToggled.value = true;
    listImageZoomImages.value = images;
}

return { __sfc: true,OrderResultMode, props, showingSalesDocumentsByMaterial, setShowingSalesDocumentsByMaterial, totalAmountOfOrders, ordersByMaterial, totalAmountOfMaterials, filterValue, timer, trackRequest, listImageZoomModalToggled, listImageZoomImages, orderData, showSalesDocumentsByMaterial, orders, totalAmount, tableConfig, tableClass, numberOfOrders, resultMode, hasResults, hasNoResults, startDate, endDate, isResultTruncated, fullScreenZoomImages, router, onRelativeUrlChange, onFilterValueChange, search, onFilter, onFormSubmit, onListImageZoom, OrderToolbar, SalesDocumentTable }
}

})